import { moduleHelp } from './registry'
import { orderHelpGroups, topicsFor } from './helpSource'

// ── La documentación, ordenada ───────────────────────────────────────────────
//
// Los módulos declaran sus temas; aquí se agrupan y se ordenan para el índice.
// La regla es la misma que rige el menú: lo que hay es lo que aportan los
// módulos instalados, y una instancia sin `work-orders` no tiene su
// documentación sin que nadie haya tenido que quitarla.
//
// «Instalado» es la parte que hay que preguntar. El registro sabe qué módulos
// venían en el repositorio al compilar, que es otra cosa: la misma imagen corre
// varias instancias y cada una monta los suyos (ver `instance.js`). Por eso
// todas las funciones de aquí admiten la lista de lo que corre de verdad, y la
// lectura que la consigue vive fuera — esto es una ordenación, y una ordenación
// se prueba sin red y sin montar Vue.

/**
 * Un tema por su id, o nada — que es lo que pasa con una dirección inventada, y
 * también con la de un módulo que esta instancia no monta.
 *
 * Lo segundo importa tanto como lo primero: las direcciones de la ayuda se
 * mandan por WhatsApp a quien empieza (`/help/crm`), y una que llegue a una
 * instancia sin ese módulo tiene que contestar lo mismo que una inventada.
 */
export function helpTopic(id, installed = null) {
  return topicsFor(moduleHelp, installed).find(topic => topic.id === String(id)) ?? null
}

/** Todos, en el orden en que se enseñan. */
export const helpTopics = (installed = null) => [...topicsFor(moduleHelp, installed)]

/**
 * Los temas por módulo, con el nombre del módulo como cabecera.
 *
 * El nombre sale de `modules.<módulo>`, que es la clave que el propio módulo
 * rellena para las pantallas de la plataforma (Roles, Ajustes, Auditoría). Así
 * el índice dice «Órdenes de trabajo» y no `work-orders`, sin que el armazón
 * tenga que conocer a nadie.
 *
 * `t` entra como parámetro en vez de importar el i18n: este fichero es una
 * ordenación y se prueba sin montar Vue. `locale` es el idioma en que se
 * ordenan los nombres (ver `orderHelpGroups`).
 */
export function helpGroups(t, installed = null, locale = undefined) {
  const byModule = new Map()

  for (const topic of topicsFor(moduleHelp, installed)) {
    const name = topic.module ?? 'platform'
    if (!byModule.has(name)) byModule.set(name, [])
    byModule.get(name).push(topic)
  }

  return orderHelpGroups([...byModule.entries()].map(([name, topics]) => ({
    name,
    // `t` devuelve la clave cuando no hay traducción: entonces vale más el
    // nombre de la carpeta que un `modules.x` crudo en mitad del índice.
    label: t(`modules.${name}`) === `modules.${name}` ? name : t(`modules.${name}`),
    topics,
  })), locale)
}
